import { useState } from "react";

import { offerKeyFacts } from "@sill/shared/components/marketplace/offer-card";
import { formatSentAgo, type ConnectionRequestDTO } from "../../lib/marketplace-status";
import { Button } from "../ui/button";

/**
 * RequestCard — the `waiting_agent` body block (plan 435 M4, mfh-request-card;
 * roadmap M4 bullet 1; tech-spec Components "RequestCard").
 *
 * Built from the M0 additive request DTO fields (`pro_display_name`,
 * `expires_at`, `offer`). Degrades gracefully when a pre-M0 server (or a
 * row minted before M0 shipped) omits them: no name, no deadline, no
 * frozen-offer box, but Withdraw stays available regardless.
 *
 * Withdraw is a two-step confirm, mirroring `MarketplaceStatusCard`'s
 * `WaitingAgentCard` idiom exactly (idle → confirming → withdrawing →
 * error), but this component owns its own copy since the request card's
 * surrounding context (standalone body block, not a banner) differs.
 */
export interface RequestCardProps {
  request: ConnectionRequestDTO;
  /** Withdraws the request. Resolves on success (the caller unmounts this
   *  card and lets the hero re-derive); rejects on failure (the card shows
   *  the inline error and stays mounted). */
  onWithdraw: (requestId: string) => Promise<void>;
}

type WithdrawFlow =
  | { step: "idle" }
  | { step: "confirming" }
  | { step: "withdrawing" }
  | { step: "error"; message: string };

function formatDeadline(expiresAt: string | undefined): string | null {
  if (!expiresAt) return null;
  const parsed = new Date(expiresAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** First name only — mirrors `lib/home-hero-state.ts`'s `firstName` helper
 *  (not imported — that module intentionally exports no non-component
 *  value for this file to share without a circular concern; kept small and
 *  duplicated rather than threading a new shared utility for one line). */
function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName;
}

export function RequestCard({ request, onWithdraw }: RequestCardProps) {
  const [flow, setFlow] = useState<WithdrawFlow>({ step: "idle" });

  const deadline = formatDeadline(request.expires_at);
  const hasOffer = Boolean(request.offer && request.offer.items);
  const facts = hasOffer
    ? offerKeyFacts({
        commissionPct: request.offer?.commission_pct,
        items: request.offer!.items,
      })
    : [];
  const degraded = !request.pro_display_name && !deadline && facts.length === 0;

  const handleWithdraw = async () => {
    setFlow({ step: "withdrawing" });
    try {
      await onWithdraw(request.id);
      setFlow({ step: "idle" });
    } catch {
      setFlow({
        step: "error",
        message: "Couldn't withdraw — please try again.",
      });
    }
  };

  return (
    <section
      className="marketplace-surface space-y-3 p-4 sm:p-5"
      data-degraded={degraded || undefined}
      data-testid="home-request-card"
    >
      {degraded ? (
        <p className="text-base font-semibold text-foreground" data-testid="request-card-degraded-headline">
          {request.created_at ? `Request sent ${formatSentAgo(request.created_at)}` : "Request sent"}
          {" · waiting for the agent to confirm"}
          {request.buyer_tier ? ` · ${request.buyer_tier.toUpperCase()}` : ""}
        </p>
      ) : (
        <>
          <p className="text-base font-semibold text-foreground" data-testid="request-card-headline">
            {request.pro_display_name
              ? `Waiting on ${request.pro_display_name}`
              : "Waiting on the agent"}
          </p>
          {deadline ? (
            <p className="text-sm text-muted-foreground" data-testid="request-card-deadline">
              They have until {deadline} to confirm.
            </p>
          ) : null}
          {facts.length > 0 ? (
            <div
              className="space-y-1 rounded-xl border border-dashed border-primary/30 bg-primary/5 px-3.5 py-2.5"
              data-testid="request-card-offer-box"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-primary">
                Offer you accepted · Frozen
              </p>
              <p className="text-sm text-foreground">{facts.join(" · ")}</p>
            </div>
          ) : null}
          <p className="text-sm text-muted-foreground" data-testid="request-card-passport-note">
            Your passport stays live to other agents until{" "}
            {request.pro_display_name ? firstName(request.pro_display_name) : "they"}{" "}
            confirm{request.pro_display_name ? "s" : ""}.
          </p>
        </>
      )}

      {flow.step === "idle" && (
        <button
          className="text-sm font-semibold text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          data-testid="request-card-withdraw-trigger"
          onClick={() => setFlow({ step: "confirming" })}
          type="button"
        >
          Withdraw request
        </button>
      )}

      {flow.step === "confirming" && (
        <div className="space-y-2" data-testid="request-card-withdraw-confirm">
          <p className="text-sm font-medium text-foreground">
            Withdraw your request? You&rsquo;ll be able to send a new one anytime.
          </p>
          <div className="flex gap-2">
            <Button
              data-testid="request-card-withdraw-confirm-button"
              onClick={() => void handleWithdraw()}
              size="sm"
              variant="destructive"
            >
              Yes, withdraw
            </Button>
            <Button
              data-testid="request-card-withdraw-cancel-button"
              onClick={() => setFlow({ step: "idle" })}
              size="sm"
              variant="ghost"
            >
              Keep waiting
            </Button>
          </div>
        </div>
      )}

      {flow.step === "withdrawing" && (
        <p className="text-sm text-muted-foreground" data-testid="request-card-withdraw-loading">
          Withdrawing…
        </p>
      )}

      {flow.step === "error" && (
        <div className="space-y-2" data-testid="request-card-withdraw-error">
          <p className="text-sm text-destructive">{flow.message}</p>
          <button
            className="text-sm font-semibold text-primary hover:text-primary/80"
            data-testid="request-card-withdraw-retry"
            onClick={() => void handleWithdraw()}
            type="button"
          >
            Try again
          </button>
        </div>
      )}
    </section>
  );
}
